import { Modal } from 'antd';
import type { GeoCoordinate } from 'common/models';
import { DEFAULT_COORDINATES } from 'components/MapContainer/MapContainer';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import type { PhoneNumber } from 'pages/customers/models';

import { useCustomTranslation } from './translationHelper';

export const prohibitedSlugNames = [
  'customertypes',
  'customercategories',
  'serviceobjectcategories',
  'serviceobjectcustomstates',
  'ordercategories',
  'materialcategories',
  'materialsuppliers',
  'serviceobjectmanufacturers',
  'interventionCustomStates',
  'workitems',
  'vatoptions',
  'customcodebooks',
  'users',
  'interventioncategories',
  'ordercustomstates',
  'orderrequestcustomstates',
  'orderrequestcategories',
  'groups',
  'skills',
  'paymenttypes',
  'serviceobjectrevisioncategories'
];

export const removeDiacritics = (str?: string): string => {
  return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
};

export const removeWhiteSpaces = (str: string): string => str.replace(/\s/g, '');

export const removeUnnecessaryWhiteSpaces = (str: string): string => str.replace(/\s+/g, '');

export const slugify = (text: string) =>
  removeDiacritics(text)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '_');

export const MIN_PASSWORD_LENGTH = 5;
export const COMPLEX_PASSWORD_LENGTH = 8;

export const validateTenantCode = (tenantCode: string) => {
  return /^[a-z0-9](?!.*--)[a-z0-9-]*[a-z0-9]$/.test(tenantCode);
};

export const useValidateAddress = () => {
  const { t } = useCustomTranslation();

  const validateDefaultCoords = async (coords: GeoCoordinate) => {
    if (
      coords.latitude === DEFAULT_COORDINATES.lat &&
      coords.longitude === DEFAULT_COORDINATES.lng
    ) {
      return new Promise((resolve) => {
        Modal.confirm({
          title: t('address.defaultCoordsWarning'),
          okText: t('misc.okText'),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
    }

    return new Promise((resolve) => {
      resolve(true);
    });
  };

  const validateZeroCoords = (coords: GeoCoordinate) => {
    if (coords.latitude === 0 && coords.longitude === 0) {
      return false;
    }

    return true;
  };

  return {
    validateDefaultCoords,
    validateZeroCoords
  };
};

export const transformStringForSubmit = (value?: string | null) => {
  if (value === undefined || value === '') {
    return null;
  }

  return value;
};

export const validateNullability = (originalValue?: any) => {
  if (originalValue) {
    return null;
  }

  return undefined;
};

// Only one pattern rule can be used at one time, if we want more complex validation we must to use validate function

export const parseJiraIssueKeys = (value: string) => [
  ...new Set(
    value
      .trim()
      .toUpperCase()
      .split(/[\s,;]+/)
      .filter(Boolean)
  )
];

export const useValidators = () => {
  const { tError, tSupportTicket } = useCustomTranslation();

  const jiraIssueKeys = (value: string) => {
    const keys = parseJiraIssueKeys(value);
    if (!keys.length || keys.length > 10) return tSupportTicket('jira_keys_count');
    return keys.every((key) => key.length <= 255 && /^[A-Z][A-Z0-9_]*-[1-9][0-9]*$/.test(key))
      ? undefined
      : tSupportTicket('jira_key_invalid');
  };

  const email = (value: string) =>
    value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,9}$/i.test(value)
      ? tError('emailAddress')
      : undefined;

  const tenantCode = (value: string) =>
    value && !/^[a-z0-9](?!.*--)[a-z0-9-]*[a-z0-9]$/.test(value.toLocaleLowerCase())
      ? tError('alphanumeric_with_dash')
      : undefined;

  const required = {
    value: true,
    message: tError('required')
  };

  const minLength = (min: number) => ({
    value: min,
    message: tError('min_length', { min })
  });

  const passwordMinLength = (password: string, min: number) =>
    password.length < min ? tError('min_length', { min }) : undefined;

  const maxLength = (max: number) => ({
    value: max,
    message: tError('max_length', { max })
  });

  const max = (max: number) => ({
    value: max,
    message: tError('max_value', { max })
  });

  const min = (min: number) => ({
    value: min,
    message: tError('min_value', { min })
  });

  const requiredArray = (value?: any[]) =>
    Array.isArray(value) && value.length > 0 ? true : tError('required');

  const lowercaseChar = {
    pattern: {
      value: /[a-z]/g,
      message: tError('missing_lowercase_char')
    }
  };

  const passwordLowercaseChar = (password: string) =>
    /[a-z]/g.test(password) ? undefined : tError('missing_lowercase_char');

  const uppercaseChar = {
    pattern: {
      value: /[A-Z]/g,
      message: tError('missing_uppercase_char')
    }
  };

  const passwordUppercaseChar = (password: string) =>
    /[A-Z]/g.test(password) ? undefined : tError('missing_uppercase_char');

  const specialChar = {
    pattern: {
      value: /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g,
      message: tError('missing_special_char')
    }
  };

  const passwordSpecialChar = (password: string) =>
    /[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g.test(password)
      ? undefined
      : tError('missing_special_char');

  const numberChar = {
    pattern: {
      value: /[0-9]/g,
      message: tError('missing_number_char')
    }
  };

  const passwordNumberChar = (password: string) =>
    /[0-9]/g.test(password) ? undefined : tError('missing_number_char');

  const extendedAlphaNumeric = (value: string) => {
    if (value && !/^[a-zA-Z0-9_]+$/i.test(value)) {
      return tError('only_extend_alphanumeric');
    }

    return undefined;
  };

  const slugName = (value: string) =>
    value && prohibitedSlugNames.includes(value.toLocaleLowerCase())
      ? 'Zadaný slug není povolen'
      : undefined;

  const url = (value: string) => {
    const urlPattern = /^(https?:\/\/)([\w\-]+\.)+[\w\-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

    if (value && !urlPattern.test(value)) {
      return tError('not_valid_url');
    }

    return undefined;
  };

  const urlWithIp = (value: string) => {
    if (!value) return undefined;

    // Pattern for domain-based URL
    const domainPattern =
      /^(https?:\/\/)(([\w\-]+\.)+[\w\-]+|localhost)(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

    // Pattern for IPv4 address with optional port
    const ipv4Pattern =
      /^(https?:\/\/)((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

    // Pattern for IPv6 address with optional port (enclosed in brackets)
    const ipv6Pattern =
      /^(https?:\/\/)\[([0-9A-Fa-f]{0,4}:){2,7}[0-9A-Fa-f]{0,4}\](:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

    if (!domainPattern.test(value) && !ipv4Pattern.test(value) && !ipv6Pattern.test(value)) {
      return tError('not_valid_url');
    }

    return undefined;
  };

  const validatePhoneNumber = (value?: PhoneNumber, required?: boolean) => {
    let isValid = false;

    if (required && !value?.number) {
      return tError('required');
    }

    if (value?.number) {
      try {
        const phoneNumber = parsePhoneNumber(`${value.prefix}${value.number}`);

        isValid = phoneNumber.isValid();
      } catch {
        isValid = false;
      }
    }

    return value?.number && !isValid ? tError('invalid_phone_number') : undefined;
  };

  const emailOrPhoneFilled = (values: { email?: string | null; phone?: PhoneNumber | null }) => {
    if (
      (values.email === undefined || values.email === '') &&
      (!values.phone || values.phone.number === undefined || values.phone.number === '')
    ) {
      return tError('emailOrPhoneMustBeFilledError');
    }

    return undefined;
  };

  const emailOrPasswordFilled = (values: { email?: string | null; password?: string | null }) => {
    if (!values.email && !values.password) {
      return tError('emailOrPasswordMustBeFilledError');
    }

    return undefined;
  };

  const passwordComplexityValidation = (value: string) => {
    if (!/[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g.test(value)) {
      return tError('missing_special_char');
    }

    if (!/[0-9]/g.test(value)) {
      return tError('missing_number_char');
    }

    if (!/[A-Z]/g.test(value)) {
      return tError('missing_uppercase_char');
    }

    if (!/[a-z]/g.test(value)) {
      return tError('missing_lowercase_char');
    }
  };

  const samePasswordCheck = (value: string, originalPassword?: string) => {
    if (value !== originalPassword) {
      return tError('passwords_not_match');
    }
  };

  const isIPorCIDR = (value: string) => {
    if (!value || typeof value !== 'string') return tError('invalid_ip_address');

    const octet = '(25[0-5]|2[0-4]\\d|1?\\d?\\d)';
    const ipv4Pattern = `^(${octet}(\\.${octet}){3})$`;
    const ipv4CidrPattern = `^(${octet}(\\.${octet}){3})\\/(?:[0-9]|[12][0-9]|3[0-2])$`;

    const IPv4_REGEX = new RegExp(ipv4Pattern);
    const IPv4_CIDR_REGEX = new RegExp(ipv4CidrPattern);

    // IPv6 pattern (kept anchored)
    const IPv6_REGEX = new RegExp(
      '^(' +
        '([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4}|' +
        '([0-9A-Fa-f]{1,4}:){1,7}:|' +
        '([0-9A-Fa-f]{1,4}:){1,6}:[0-9A-Fa-f]{1,4}|' +
        '([0-9A-Fa-f]{1,4}:){1,5}(:[0-9A-Fa-f]{1,4}){1,2}|' +
        '([0-9A-Fa-f]{1,4}:){1,4}(:[0-9A-Fa-f]{1,4}){1,3}|' +
        '([0-9A-Fa-f]{1,4}:){1,3}(:[0-9A-Fa-f]{1,4}){1,4}|' +
        '([0-9A-Fa-f]{1,4}:){1,2}(:[0-9A-Fa-f]{1,4}){1,5}|' +
        '[0-9A-Fa-f]{1,4}:((:[0-9A-Fa-f]{1,4}){1,6})|' +
        ':((:[0-9A-Fa-f]{1,4}){1,7}|:)' +
        ')$'
    );
    const IPv6_CIDR_REGEX = new RegExp(
      `^(${IPv6_REGEX.source})\\/(?:[0-9]|[1-9][0-9]|1[01][0-9]|12[0-8])$`
    );

    const s = value.trim();
    if (
      IPv4_REGEX.test(s) ||
      IPv4_CIDR_REGEX.test(s) ||
      IPv6_REGEX.test(s) ||
      IPv6_CIDR_REGEX.test(s)
    ) {
      return undefined; // valid
    }
    return tError('invalid_ip_address'); // invalid
  };

  const username = (value: string) => {
    if (!value) return undefined;

    if (!/^[a-zA-Z0-9_.@-]+$/i.test(value)) {
      return tError('username_invalid_characters');
    }
  };

  return {
    required,
    jiraIssueKeys,
    email,
    tenantCode,
    lowercaseChar,
    uppercaseChar,
    specialChar,
    numberChar,
    username,
    extendedAlphaNumeric,
    slugName,
    minLength,
    maxLength,
    max,
    min,
    validatePhoneNumber,
    emailOrPhoneFilled,
    emailOrPasswordFilled,
    passwordComplexityValidation,
    samePasswordCheck,
    isIPorCIDR,
    url,
    urlWithIp,
    passwordMinLength,
    passwordLowercaseChar,
    passwordUppercaseChar,
    passwordSpecialChar,
    passwordNumberChar,
    requiredArray
  };
};

export const validateTechnicianFunction = (
  roles: { isAdmin?: boolean; isManager: boolean; isReadonly: boolean },
  enabled: boolean
): boolean => {
  const resetFunction = roles.isAdmin || roles.isManager || roles.isReadonly;

  if (resetFunction && enabled) {
    return false;
  }

  return enabled;
};
